import * as Sentry from '@sentry/react';
import { Analytics as VercelAnalytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createSentryMutationCache,
  getSentryReactQueryOptions,
} from "@/lib/sentry";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import SiteMeta from "@/components/SiteMeta";
import { AuthProvider } from "@/hooks/useAuth";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { DashboardReferenceProvider } from "@/contexts/DashboardReferenceContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import LegacyAppRedirect from "@/components/LegacyAppRedirect";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import DemoRedirect from "./pages/DemoRedirect";

import Pricing from "./pages/Pricing";
import Features from "./pages/Features";
import InsideVesta from "./pages/InsideVesta";
import Company from "./pages/Company";
import Contact from "./pages/Contact";
import Partners from "./pages/Partners";
import HotelPartnerMarketplace from "./pages/HotelPartnerMarketplace";
import About from "./pages/About";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import PressRelease from "./pages/PressRelease";
import Careers from "./pages/Careers";
import IntegrationsPublic from "./pages/IntegrationsPublic";

import Welcome from "./pages/Welcome";
import ConsumerJoin from "./pages/ConsumerJoin";
import JobRole from "./pages/JobRole";
import AICfoCall from "./pages/AICfoCall";
import Privacy from "./pages/Privacy";
import Support from "./pages/Support";
import Security from "./pages/Security";
import Trust from "./pages/Trust";
import Status from "./pages/Status";
import HotelOnboarding from "./pages/HotelOnboarding";
import StepByStepGuide from "./pages/StepByStepGuide";
import TermsOfService from "./pages/TermsOfService";
import Press from "./pages/Press";
import NotFound from "./pages/NotFound";
import IntegrationsQbCallback from "./pages/IntegrationsQbCallback";
import BudgetPage from "./pages/Budget";
import AnomaliesPage from "./pages/Anomalies";
import ReportsPage from "./pages/Reports";
import OwnerReportPage from "./pages/OwnerReport";
import CompSetPage from "./pages/CompSet";
import EventsPage from "./pages/Events";
import TeamPage from "./pages/Team";
import SharedConversation from "./pages/SharedConversation";
import { HotelAppShell } from "@/components/hotel/HotelAppShell";
import SettingsPage from "./pages/Settings";

const SentryDebugLazy = import.meta.env.DEV
  ? lazy(() => import("./pages/SentryDebug"))
  : null;

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Integrations = lazy(() => import("./pages/Integrations"));
const HotelChatPage = lazy(() => import("./pages/HotelChatPage"));
const AdminHub = lazy(() => import("./pages/AdminHub"));

const RouteFallback = () => (
  <div className="flex min-h-[40vh] items-center justify-center text-sm text-vesta-navy/65">
    Loading…
  </div>
);

const queryClient = new QueryClient({
  mutationCache: createSentryMutationCache(),
  defaultOptions: getSentryReactQueryOptions(),
});

/** Web Analytics needs route + path so SPA navigations fire pageviews (see @vercel/analytics/react). */
const VercelWebAnalytics = () => {
  const { pathname, search } = useLocation();
  return <VercelAnalytics route={pathname} path={`${pathname}${search}`} />;
};

const AppContent = () => (
  <>
    <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/demo" element={<DemoRedirect />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/features" element={<Features />} />
        <Route path="/product/integrations" element={<IntegrationsPublic />} />
        <Route path="/inside" element={<InsideVesta />} />
        <Route path="/company" element={<Company />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/about" element={<About />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/press/:slug" element={<PressRelease />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/careers/:slug" element={<JobRole />} />
        
        <Route path="/admin" element={
          <ProtectedRoute>
            <Suspense fallback={<RouteFallback />}>
              <AdminHub />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/welcome/:slug" element={<Welcome />} />
        <Route path="/join/:slug" element={<ConsumerJoin />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/support" element={<Support />} />
        <Route path="/security" element={<Security />} />
        <Route path="/trust" element={<Trust />} />
        <Route path="/status" element={<Status />} />
        {/* Hotel app — one persistent shell so sidebar pages fade smoothly */}
        <Route element={
          <ProtectedRoute>
            <HotelAppShell />
          </ProtectedRoute>
        }>
          <Route path="dashboard" element={
            <Suspense fallback={<RouteFallback />}>
              <Dashboard />
            </Suspense>
          } />
          <Route path="integrations/qb-callback" element={<IntegrationsQbCallback />} />
          <Route path="integrations" element={
            <Suspense fallback={<RouteFallback />}>
              <Integrations />
            </Suspense>
          } />
          <Route path="budget" element={<BudgetPage />} />
          <Route path="anomalies" element={<AnomaliesPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="reports/:reportId" element={<OwnerReportPage />} />
          <Route path="comp-set" element={<CompSetPage />} />
          <Route path="events" element={<EventsPage />} />
          <Route path="team" element={<TeamPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="marketplace" element={<HotelPartnerMarketplace />} />
          <Route path="chat" element={
            <Suspense fallback={<RouteFallback />}>
              <HotelChatPage />
            </Suspense>
          } />
        </Route>
        <Route path="/app" element={
          <ProtectedRoute>
            <LegacyAppRedirect />
          </ProtectedRoute>
        } />
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <HotelOnboarding />
          </ProtectedRoute>
        } />
        <Route path="/payment-selection" element={<Navigate to="/integrations" replace />} />
        <Route path="/how-to-get-started" element={<StepByStepGuide />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/press" element={<Press />} />
        <Route path="/call" element={
          <ProtectedRoute>
            <AICfoCall />
          </ProtectedRoute>
        } />
        <Route path="/quickbooks" element={<Navigate to="/dashboard" replace />} />
        <Route path="/shared/:token" element={<SharedConversation />} />
        
        {/* Old /hpt1 route removed - now served at / */}
        <Route path="/docs/*" element={<Navigate to="/support" replace />} />
        {import.meta.env.DEV && SentryDebugLazy && (
          <Route
            path="/__debug/sentry"
            element={
              <Suspense
                fallback={
                  <div className="flex min-h-[40vh] items-center justify-center text-sm text-vesta-navy/65">
                    Loading…
                  </div>
                }
              >
                <SentryDebugLazy />
              </Suspense>
            }
          />
        )}
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
  </>
);

const App = () => (
  <Sentry.ErrorBoundary fallback={<div className="flex min-h-screen items-center justify-center bg-vesta-cream px-4 text-center font-sans text-sm text-vesta-navy/85">Something went wrong. Please refresh.</div>}>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SettingsProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <SpeedInsights />
            <BrowserRouter>
              <VercelWebAnalytics />
              <ScrollToTop />
              <SiteMeta />
              <DashboardReferenceProvider>
                <AppContent />
              </DashboardReferenceProvider>
            </BrowserRouter>
          </TooltipProvider>
        </SettingsProvider>
      </AuthProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
